import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/layout/Sidebar/Sidebar";
import TopHeader from "../components/layout/Header/TopHeader";
import LogoutConfirmModal from "../components/auth/LogoutConfirmModal";
import {
  APP_SETTINGS_EVENT,
  getAppSettings,
} from "../services/appSettingsService";
import { getStoredAuthUser } from "../services/authService";
import styles from "./AdminLayout.module.css";

const AdminLayout = ({ title, actions, children, propertyContext = null }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [authUser, setAuthUser] = useState(getStoredAuthUser());
  const [theme, setTheme] = useState(() => {
    try {
      return localStorage.getItem("theme") || "light";
    } catch {
      return "light";
    }
  });
  const [globalSearch, setGlobalSearch] = useState("");
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const navigate = useNavigate();

  // Apply theme to document
  useEffect(() => {
    document.documentElement.dataset.theme = theme === "dark" ? "dark" : "light";
    if (theme === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
    localStorage.setItem("theme", theme);
  }, [theme]);

  // Load app settings and auth user
  useEffect(() => {
    const loadSettings = async () => {
      const nextSettings = await getAppSettings();
      setSettings(nextSettings);
      setAuthUser(getStoredAuthUser());
    };

    loadSettings();
    window.addEventListener(APP_SETTINGS_EVENT, loadSettings);

    return () => {
      window.removeEventListener(APP_SETTINGS_EVENT, loadSettings);
    };
  }, []);

  const handleToggleTheme = () => {
    setTheme((prev) => (prev === "dark" ? "light" : "dark"));
  };

  const handleOpenProfile = () => {
    navigate("/profile");
    setMobileOpen(false);
  };

  const mainClassName = [
    styles.main,
    isCollapsed ? styles.mainCollapsed : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={styles.page}>
      {/* Modular Sidebar with grouped pillars or property modules */}
      <Sidebar
        isCollapsed={isCollapsed}
        onToggleCollapse={() => setIsCollapsed(!isCollapsed)}
        mobileOpen={mobileOpen}
        onCloseMobile={() => setMobileOpen(false)}
        authUser={authUser}
        settings={settings}
        onOpenProfile={handleOpenProfile}
        onLogoutClick={() => setShowLogoutModal(true)}
        propertyContext={propertyContext}
      />

      {/* Main Layout Canvas */}
      <div className={mainClassName}>
        <TopHeader
          title={title}
          onOpenMobile={() => setMobileOpen(true)}
          onToggleCollapse={() => setIsCollapsed(!isCollapsed)}
          searchQuery={globalSearch}
          onSearchChange={setGlobalSearch}
          theme={theme}
          onToggleTheme={handleToggleTheme}
          authUser={authUser}
          onOpenProfile={handleOpenProfile}
        />

        <main className={styles.content}>
          {actions && (
            <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginBottom: 16 }}>
              {actions}
            </div>
          )}
          {children}
        </main>

        <footer className={styles.footer}>
          © {new Date().getFullYear()} myAsmitA Operations Platform • All Rights Reserved
        </footer>
      </div>

      <LogoutConfirmModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
      />
    </div>
  );
};

export default AdminLayout;
